import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { createEnumParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import ExpertSelect from "@components/fields/ExpertSelect";
import Select from "@components/fields/Select";
import styles from "@components/forms/AdminJourneyFilters.module.css";
import Form from "@components/forms/Form";
import useMyExpert from "@hooks/useMyExpert";
import { ExpertGlobalRoleEnum, TimeLogBillableStatusEnum } from "@typing/Generated";

type FormData = {
  billable?: TimeLogBillableStatusEnum | null;
  expertId?: string | null;
};

type Props = {
  filters: FormData;
  onChange: (filterValues: FormData) => void;
};

const TimeLogFilters = ({ filters, onChange }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const billableParam = createEnumParam<TimeLogBillableStatusEnum>(Object.values(TimeLogBillableStatusEnum));

  const [query, setQuery] = useQueryParams({
    billable: withDefault({ ...billableParam, urlName: "timeLogBillable" }, filters.billable),
    expertId: withDefault({ ...StringParam, urlName: "timeLogExpertId" }, filters.expertId),
    expertName: StringParam
  });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const billable = form.watch("billable");
  const expertId = form.watch("expertId");

  useEffect(() => {
    const values = { billable, expertId };
    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
  }, [billable, expertId, filters, form, onChange, setQuery]);

  const billableOptions = Object.values(TimeLogBillableStatusEnum).map(option => ({
    label: intl.formatMessage({ id: `models.timeLog.billableOptions.${option}` }),
    value: option
  }));

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form className={styles.form} hookForm={form} onSubmit={() => {}}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager && (
            <IonCol>
              <ExpertSelect<FormData>
                allowAny
                control={form.control}
                error={form.formState.errors.expertId}
                initialOption={{ label: query.expertName ?? undefined, value: query.expertId ?? null }}
                label={intl.formatMessage({ id: "forms.expertJourneysFilters.expertOwnerLabel" })}
                name="expertId"
                onSelection={(option: { label: string }) => {
                  setQuery({ ...query, ...{ expertName: option.label } });
                }}
              />
            </IonCol>
          )}
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.billable}
              label={intl.formatMessage({ id: "forms.timeLogFilters.billableLabel" })}
              name="billable"
              options={billableOptions}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default TimeLogFilters;
